import React, { useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useListJobOpeningsQuery,
  useCreateJobOpeningMutation,
  useUpdateJobOpeningMutation,
  useDeleteJobOpeningMutation,
  useUploadJobOpeningJdMutation,
  useGetJobOpeningJdUrlMutation,
  useDeleteJobOpeningJdMutation,
  useUploadJobOpeningVideoMutation,
  useDeleteJobOpeningVideoMutation,
  useNotifyNewJobPostingMutation,
  useLoadCompanyDirectoryQuery,
  useListJobApplicationsQuery,
} from '../app/api';

// Labels for the application questions/acknowledgements, mirrored from the
// employee apply form so HR sees the same wording the applicant answered.
const APPLICATION_QUESTIONS = [
  { key: 'reason_interested', label: 'Why interested in this position?' },
  { key: 'strengths', label: 'Skills, experience & accomplishments' },
  { key: 'growth_alignment', label: 'Alignment with growth & career goals' },
];
const APPLICATION_ACKS = [
  { key: 'ack_no_guarantee', label: 'Understands application does not guarantee an interview or promotion' },
  { key: 'ack_transition_coordinated', label: 'Understands transition date is coordinated between departments' },
  { key: 'ack_reviewed_requirements', label: 'Reviewed the job description and meets the minimum requirements' },
];

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const JD_ACCEPT = '.pdf,.doc,.docx,.txt,.rtf,.odt';
const JD_MAX_MB = 15;

const VIDEO_ACCEPT = '.mp4,.mov,.webm,.m4v';
const VIDEO_MAX_MB = 200;

// Per-row visibility tiers. The label/colors are deliberately loud — HR needs
// to see at a glance exactly who can read each posting.
export const VISIBILITY_META = {
  hr:       { label: 'HR Only',        icon: 'lock-fill',     bg: brand.deepBlue,  fg: 'white' },
  managers: { label: 'Managers Only',  icon: 'people-fill',   bg: brand.goldenrod, fg: brand.deepBlue },
  all:      { label: 'All Employees',  icon: 'globe',         bg: brand.green,     fg: 'white' },
};

const POSITION_TYPES = [
  { value: 'in_office', label: 'In office' },
  { value: 'hybrid',    label: 'Hybrid' },
  { value: 'remote',    label: 'Remote' },
];
const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
];

function labelFor(list, value) {
  const m = list.find((o) => o.value === value);
  return m ? m.label : null;
}

export function VisibilityBadge({ visibility }) {
  const meta = VISIBILITY_META[visibility] || { label: visibility || 'Unknown', icon: 'question', bg: brand.charcoal, fg: 'white' };
  return (
    <span style={{
      background: meta.bg, color: meta.fg, fontSize: '0.72rem', fontWeight: 700,
      padding: '0.2rem 0.6rem', borderRadius: '999px', whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    }}>
      <i className={`bi bi-${meta.icon}`} /> {meta.label}
    </span>
  );
}
VisibilityBadge.propTypes = { visibility: PropTypes.string };

const blankForm = {
  job_title: '',
  description: '',
  potential_start_date: '',
  salary_range: '',
  manager_id: '',
  location: '',
  position_type: '',
  employment_type: '',
  is_intern: false,
  is_active: true,
  // New postings start HR-only; HR explicitly widens visibility when ready.
  visibility: 'hr',
  fulfilled_by: '',
};

function toForm(opening) {
  return {
    job_title: opening.job_title || '',
    description: opening.description || '',
    potential_start_date: opening.potential_start_date || '',
    salary_range: opening.salary_range || '',
    manager_id: opening.manager_id != null ? String(opening.manager_id) : '',
    location: opening.location || '',
    position_type: opening.position_type || '',
    employment_type: opening.employment_type || '',
    is_intern: Boolean(opening.is_intern),
    is_active: Boolean(opening.is_active),
    visibility: opening.visibility || 'all',
    fulfilled_by: opening.fulfilled_by || '',
  };
}

function toBody(form) {
  return {
    job_title: form.job_title.trim(),
    description: form.description || null,
    potential_start_date: form.potential_start_date || null,
    salary_range: form.salary_range || null,
    manager_id: form.manager_id ? Number(form.manager_id) : null,
    location: form.location || null,
    position_type: form.position_type || null,
    employment_type: form.employment_type || null,
    is_intern: Boolean(form.is_intern),
    is_active: Boolean(form.is_active),
    visibility: form.visibility,
    fulfilled_by: form.fulfilled_by || null,
  };
}

// One submitted application: applicant identity, their answers, and the
// acknowledgement state. Rendered inside a posting's expanded row.
function ApplicationCard({ app }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2ddd4', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <span style={{ fontWeight: 700, color: brand.deepBlue }}>{app.applicant_name}</span>
          {app.applicant_email && (
            <a href={`mailto:${app.applicant_email}`} style={{ color: brand.tealBlue, fontSize: '0.8rem', marginLeft: '0.5rem' }}>
              {app.applicant_email}
            </a>
          )}
        </div>
        {app.created && (
          <span style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
            Applied {new Date(app.created).toLocaleDateString()}
          </span>
        )}
      </div>

      <div style={{ marginTop: '0.6rem' }}>
        {APPLICATION_QUESTIONS.map((q) => (
          <div key={q.key} style={{ marginBottom: '0.5rem' }}>
            <div style={{ fontWeight: 700, color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{q.label}</div>
            <div style={{ color: brand.deepBlue, fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
              {app[q.key] || <span style={{ color: '#b9c3cd' }}>—</span>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        {APPLICATION_ACKS.map((a) => (
          <div key={a.key} style={{ fontSize: '0.76rem', color: app[a.key] ? brand.green : brand.red }}>
            <i className={`bi bi-${app[a.key] ? 'check-circle-fill' : 'x-circle'} me-1`} />{a.label}
          </div>
        ))}
      </div>
    </div>
  );
}
ApplicationCard.propTypes = { app: PropTypes.object.isRequired };

export default function HRJobOpenings() {
  // Management view: every posting (incl. inactive/filled) plus salary.
  const { data, refetch, isLoading } = useListJobOpeningsQuery({ manage: true });
  const openings = data?.openings || [];

  const { data: directory = [] } = useLoadCompanyDirectoryQuery();

  // Internal applications across all postings, grouped by posting id so each
  // row can show who applied and their answers.
  const { data: appsData } = useListJobApplicationsQuery();
  const appsByOpening = useMemo(() => {
    const m = {};
    for (const a of (appsData?.applications || [])) {
      (m[a.job_opening_id] = m[a.job_opening_id] || []).push(a);
    }
    return m;
  }, [appsData]);
  const [expandedApps, setExpandedApps] = useState(() => new Set());
  const toggleApps = (id) => setExpandedApps((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const [createOpening] = useCreateJobOpeningMutation();
  const [updateOpening] = useUpdateJobOpeningMutation();
  const [deleteOpening] = useDeleteJobOpeningMutation();
  const [uploadJd] = useUploadJobOpeningJdMutation();
  const [getJdUrl] = useGetJobOpeningJdUrlMutation();
  const [deleteJd] = useDeleteJobOpeningJdMutation();
  const [uploadVideo] = useUploadJobOpeningVideoMutation();
  const [deleteVideo] = useDeleteJobOpeningVideoMutation();
  const [notifyNewPosting] = useNotifyNewJobPostingMutation();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  // File chosen inside the create/edit form. The backend's upload endpoint
  // needs a posting id, so we hold the File here and upload it right after the
  // create/update returns the id.
  const [formFile, setFormFile] = useState(null);
  const [editingJd, setEditingJd] = useState(null);  // existing attached filename, when editing
  // Intro video chosen in the form (uploaded after save, like the JD file), the
  // existing video filename when editing, and a flag to remove it on save.
  const [formVideo, setFormVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const jdInputRef = useRef(null);
  const formFileRef = useRef(null);
  const formVideoRef = useRef(null);

  const clearFormFile = () => {
    setFormFile(null);
    if (formFileRef.current) formFileRef.current.value = '';
  };

  const clearFormVideo = () => {
    setFormVideo(null);
    if (formVideoRef.current) formVideoRef.current.value = '';
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditingId(null);
    setForm(blankForm);
    setEditingJd(null);
    setEditingVideo(null);
    setRemoveVideo(false);
    clearFormFile();
    clearFormVideo();
    setError(null);
    setShowForm(true);
  };

  const openEdit = (opening) => {
    setEditingId(opening.id);
    setForm(toForm(opening));
    setEditingJd(opening.jd_filename || null);
    setEditingVideo(opening.video_filename || null);
    setRemoveVideo(false);
    clearFormFile();
    clearFormVideo();
    setError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(blankForm);
    setEditingJd(null);
    setEditingVideo(null);
    setRemoveVideo(false);
    clearFormFile();
    clearFormVideo();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.job_title.trim()) {
      setError('Job title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    const body = toBody(form);

    // 1) Save the posting. The create response carries the new id we need to
    //    attach the file to.
    let id = editingId;
    try {
      if (editingId) {
        await updateOpening({ id: editingId, body }).unwrap();
      } else {
        const created = await createOpening({ body }).unwrap();
        id = created?.id;
      }
    } catch (err) {
      setError(err?.data?.detail || 'Could not save the posting.');
      setSaving(false);
      return;
    }

    // 2) If a job-description file was chosen in the form, upload it now. A
    //    failure here leaves the (saved) posting in place — surface it so HR
    //    can retry from the row rather than losing their entries.
    if (formFile && id) {
      try {
        const fd = new FormData();
        fd.append('file', formFile);
        await uploadJd({ id, body: fd }).unwrap();
      } catch (err) {
        setError(err?.data?.detail || 'Posting saved, but the file upload failed — attach it from the row.');
        await refetch();
        setSaving(false);
        return;
      }
    }

    // 3) Apply the intro video change: upload a newly chosen video, or remove
    //    an existing one if HR cleared it. As with the JD file, a failure here
    //    leaves the saved posting in place and surfaces the error.
    if (id && formVideo) {
      try {
        const fd = new FormData();
        fd.append('file', formVideo);
        await uploadVideo({ id, body: fd }).unwrap();
      } catch (err) {
        setError(err?.data?.detail || 'Posting saved, but the video upload failed — edit the posting to retry.');
        await refetch();
        setSaving(false);
        return;
      }
    } else if (id && removeVideo && editingVideo) {
      try {
        await deleteVideo({ id }).unwrap();
      } catch (err) {
        setError(err?.data?.detail || 'Posting saved, but removing the video failed — edit the posting to retry.');
        await refetch();
        setSaving(false);
        return;
      }
    }

    setNotice(editingId ? 'Posting updated.' : 'Posting created.');
    await refetch();
    closeForm();
    setSaving(false);
  };

  const onDelete = async (opening) => {
    if (!window.confirm(`Delete “${opening.job_title}”? This removes the posting and any attached file.`)) return;
    try {
      await deleteOpening({ id: opening.id }).unwrap();
      await refetch();
    } catch {
      setError('Could not delete that posting.');
    }
  };

  // Two-step file flow: a hidden input is triggered per-row, and the chosen
  // file is uploaded against that posting's id.
  const triggerUpload = (id) => {
    setUploadingId(id);
    jdInputRef.current?.click();
  };

  const onJdChosen = useCallback(async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    const id = uploadingId;
    setUploadingId(null);
    if (!file || !id) return;
    if (file.size > JD_MAX_MB * 1024 * 1024) {
      setError(`${file.name} exceeds the ${JD_MAX_MB} MB limit.`);
      return;
    }
    setError(null);
    setNotice(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await uploadJd({ id, body: formData }).unwrap();
      setNotice('Job description file attached.');
      await refetch();
    } catch (err) {
      setError(err?.data?.detail || 'Upload failed.');
    }
  }, [uploadingId, uploadJd, refetch]);

  const onDownload = async (id) => {
    try {
      const { url } = await getJdUrl({ id }).unwrap();
      if (url) window.open(url, '_blank', 'noopener');
    } catch {
      setError('Could not open that file.');
    }
  };

  const onRemoveJd = async (opening) => {
    if (!window.confirm(`Remove the attached file from “${opening.job_title}”?`)) return;
    try {
      await deleteJd({ id: opening.id }).unwrap();
      await refetch();
    } catch {
      setError('Could not remove that file.');
    }
  };

  // Manually send the "new posting added" communication to the recipient list
  // configured on the HR Config page.
  const onAnnounce = async (opening) => {
    if (!window.confirm(`Email the new-posting notification for “${opening.job_title}” to the configured recipient list?`)) return;
    setError(null);
    setNotice(null);
    try {
      const res = await notifyNewPosting({ id: opening.id }).unwrap();
      const failed = res?.failed ? `, ${res.failed} failed` : '';
      setNotice(`Notification sent to ${res?.sent ?? 0} recipient${res?.sent === 1 ? '' : 's'}${failed}.`);
    } catch (err) {
      setError(err?.data?.detail || 'Could not send the notification.');
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.45rem 0.6rem', borderRadius: '8px',
    border: '1px solid #cdd5dd', fontSize: '0.9rem',
  };
  const labelStyle = { fontWeight: 700, color: brand.deepBlue, fontSize: '0.8rem', marginBottom: '0.25rem', display: 'block' };

  // Only employees flagged as managers in the directory are pickable. If the
  // posting being edited references someone no longer flagged as a manager,
  // keep them in the list (marked) so editing doesn't silently drop them.
  const managerOptions = directory.filter((d) => d.is_manager);
  const selectedMgrId = form.manager_id ? Number(form.manager_id) : null;
  if (selectedMgrId && !managerOptions.some((d) => d.id === selectedMgrId)) {
    const current = directory.find((d) => d.id === selectedMgrId);
    if (current) managerOptions.push({ ...current, _formerManager: true });
  }

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>Career Opportunities</h4>
          <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.875rem' }}>
            Create and manage postings. Each posting’s <strong>visibility</strong> controls who can see it —
            salary ranges are only ever shown here, to HR.
          </p>
        </div>
        {!showForm && (
          <button className="btn btn-sm" style={{ background: brand.tealBlue, color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }} onClick={openNew}>
            <i className="bi bi-plus-lg me-1" /> New Posting
          </button>
        )}
      </div>

      {notice && (
        <div style={{ marginBottom: '1rem', color: brand.green, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className="bi bi-check-circle" /> {notice}
        </div>
      )}
      {error && (
        <div style={{ marginBottom: '1rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className="bi bi-exclamation-circle" /> {error}
        </div>
      )}

      {/* ── Create / edit form ──────────────────────────────────────────────── */}
      {showForm && (
        <form onSubmit={onSubmit} className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', marginBottom: '1.25rem' }}>
          <div className="card-body">
            <h6 style={{ color: brand.deepBlue, fontWeight: 800, marginBottom: '1rem' }}>
              {editingId ? 'Edit Posting' : 'New Posting'}
            </h6>

            <div className="row g-3">
              <div className="col-md-8">
                <label style={labelStyle}>Job Title / Position *</label>
                <input style={inputStyle} value={form.job_title} onChange={(e) => set('job_title', e.target.value)} placeholder="e.g. Senior Financial Advisor" />
              </div>
              <div className="col-md-4">
                <label style={labelStyle}>Potential Start Date</label>
                <input type="date" style={inputStyle} value={form.potential_start_date} onChange={(e) => set('potential_start_date', e.target.value)} />
              </div>

              <div className="col-12">
                <label style={labelStyle}>Description / Responsibilities</label>
                <textarea style={{ ...inputStyle, minHeight: '6rem', resize: 'vertical' }} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Short summary, responsibilities, requirements…" />
              </div>

              {/* Optional longer job description file — uploaded after the
                  posting is saved (the upload endpoint needs its id). */}
              <div className="col-12">
                <label style={labelStyle}>
                  <i className="bi bi-paperclip me-1" /> Full Job Description File
                  <span style={{ fontWeight: 400, color: brand.charcoal }}> (optional)</span>
                </label>
                <div style={{ fontSize: '0.78rem', color: brand.charcoal, marginBottom: '0.4rem' }}>
                  For a longer, formatted description, attach a file (PDF, Word, RTF, or text — up to {JD_MAX_MB} MB) in addition to the summary above.
                </div>
                <input
                  ref={formFileRef}
                  type="file"
                  accept={JD_ACCEPT}
                  className="form-control form-control-sm"
                  onChange={(e) => {
                    const f = (e.target.files || [])[0] || null;
                    if (f && f.size > JD_MAX_MB * 1024 * 1024) {
                      setError(`${f.name} exceeds the ${JD_MAX_MB} MB limit.`);
                      clearFormFile();
                      return;
                    }
                    setError(null);
                    setFormFile(f);
                  }}
                />
                {(formFile || editingJd) && (
                  <div style={{ fontSize: '0.8rem', color: brand.deepBlue, marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <i className="bi bi-file-earmark-text" />
                    {formFile ? formFile.name : `Current file: ${editingJd}`}
                    {formFile ? (
                      <button type="button" className="btn btn-sm btn-link p-0" style={{ color: brand.red }} onClick={clearFormFile}>
                        remove
                      </button>
                    ) : (
                      <span style={{ color: brand.charcoal, fontWeight: 400 }}>— choose a file to replace it</span>
                    )}
                  </div>
                )}
              </div>

              {/* Optional intro video — uploaded after the posting is saved (the
                  upload endpoint needs its id). When present it plays at the top
                  of the employee posting tile. */}
              <div className="col-12">
                <label style={labelStyle}>
                  <i className="bi bi-camera-video me-1" /> Intro Video
                  <span style={{ fontWeight: 400, color: brand.charcoal }}> (optional)</span>
                </label>
                <div style={{ fontSize: '0.78rem', color: brand.charcoal, marginBottom: '0.4rem' }}>
                  Attach a short recruiting/intro video (MP4, MOV, WebM, or M4V — up to {VIDEO_MAX_MB} MB). It plays at the top of the posting in the employee view.
                </div>
                <input
                  ref={formVideoRef}
                  type="file"
                  accept={VIDEO_ACCEPT}
                  className="form-control form-control-sm"
                  onChange={(e) => {
                    const f = (e.target.files || [])[0] || null;
                    if (f && f.size > VIDEO_MAX_MB * 1024 * 1024) {
                      setError(`${f.name} exceeds the ${VIDEO_MAX_MB} MB limit.`);
                      clearFormVideo();
                      return;
                    }
                    setError(null);
                    setFormVideo(f);
                    if (f) setRemoveVideo(false);
                  }}
                />
                {(formVideo || (editingVideo && !removeVideo)) && (
                  <div style={{ fontSize: '0.8rem', color: brand.deepBlue, marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <i className="bi bi-camera-video" />
                    {formVideo ? formVideo.name : `Current video: ${editingVideo}`}
                    {formVideo ? (
                      <button type="button" className="btn btn-sm btn-link p-0" style={{ color: brand.red }} onClick={clearFormVideo}>
                        remove
                      </button>
                    ) : (
                      <>
                        <span style={{ color: brand.charcoal, fontWeight: 400 }}>— choose a file to replace it, or</span>
                        <button type="button" className="btn btn-sm btn-link p-0" style={{ color: brand.red }} onClick={() => setRemoveVideo(true)}>
                          remove on save
                        </button>
                      </>
                    )}
                  </div>
                )}
                {removeVideo && editingVideo && !formVideo && (
                  <div style={{ fontSize: '0.8rem', color: brand.red, marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <i className="bi bi-trash" /> Video will be removed when you save.
                    <button type="button" className="btn btn-sm btn-link p-0" style={{ color: brand.tealBlue }} onClick={() => setRemoveVideo(false)}>
                      keep it
                    </button>
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <label style={labelStyle}>
                  Salary Range <span style={{ color: brand.goldenrod }}><i className="bi bi-lock-fill" /> HR only</span>
                </label>
                <input style={inputStyle} value={form.salary_range} onChange={(e) => set('salary_range', e.target.value)} placeholder="e.g. $90,000–$110,000" />
              </div>
              <div className="col-md-4">
                <label style={labelStyle}>Manager</label>
                <select style={inputStyle} value={form.manager_id} onChange={(e) => set('manager_id', e.target.value)}>
                  <option value="">— Select manager —</option>
                  {managerOptions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.first_name} {d.last_name}{d._formerManager ? ' (no longer a manager)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label style={labelStyle}>Location</label>
                <input style={inputStyle} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Independence, OH" />
              </div>

              <div className="col-md-4">
                <label style={labelStyle}>Position Type</label>
                <select style={inputStyle} value={form.position_type} onChange={(e) => set('position_type', e.target.value)}>
                  <option value="">— Select —</option>
                  {POSITION_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label style={labelStyle}>Employment</label>
                <select style={inputStyle} value={form.employment_type} onChange={(e) => set('employment_type', e.target.value)}>
                  <option value="">— Select —</option>
                  {EMPLOYMENT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label style={labelStyle}>Fulfilled By</label>
                <input style={inputStyle} value={form.fulfilled_by} onChange={(e) => set('fulfilled_by', e.target.value)} placeholder="Name of the hire (if filled)" />
              </div>

              <div className="col-md-6 d-flex align-items-center gap-4" style={{ paddingTop: '1.5rem' }}>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="isIntern" checked={form.is_intern} onChange={(e) => set('is_intern', e.target.checked)} />
                  <label className="form-check-label" htmlFor="isIntern" style={{ fontWeight: 600, color: brand.charcoal }}>Internship</label>
                </div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="isActive" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                  <label className="form-check-label" htmlFor="isActive" style={{ fontWeight: 600, color: brand.charcoal }}>
                    {form.is_active ? 'Active' : 'Inactive'}
                  </label>
                </div>
              </div>
            </div>

            {/* ── Visibility (made deliberately prominent) ─────────────────────── */}
            <div style={{ marginTop: '1.25rem', padding: '1rem', borderRadius: '10px', background: '#f4f1ec', border: '1px solid #e2ddd4' }}>
              <label style={{ ...labelStyle, marginBottom: '0.5rem' }}>
                <i className="bi bi-eye me-1" /> Who can see this posting?
              </label>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {Object.entries(VISIBILITY_META).map(([key, meta]) => {
                  const selected = form.visibility === key;
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => set('visibility', key)}
                      style={{
                        flex: '1 1 180px',
                        textAlign: 'left',
                        background: selected ? meta.bg : 'white',
                        color: selected ? meta.fg : brand.charcoal,
                        border: selected ? `2px solid ${meta.bg}` : '2px solid #d7d0c4',
                        borderRadius: '10px',
                        padding: '0.6rem 0.75rem',
                        cursor: 'pointer',
                        fontWeight: 700,
                      }}
                    >
                      <i className={`bi bi-${meta.icon} me-2`} />
                      {meta.label}
                      <div style={{ fontWeight: 400, fontSize: '0.72rem', marginTop: '0.2rem', opacity: 0.9 }}>
                        {key === 'hr' && 'Only HR can see and edit this posting.'}
                        {key === 'managers' && 'Visible to employees flagged as managers (and HR).'}
                        {key === 'all' && 'Visible to every employee.'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-sm" style={{ background: brand.deepBlue, color: 'white', fontWeight: 700 }} disabled={saving}>
                {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Create posting')}
              </button>
              <button type="button" className="btn btn-sm btn-light" onClick={closeForm} disabled={saving}>Cancel</button>
            </div>
          </div>
        </form>
      )}

      {/* Hidden input shared by every row's "Upload JD" button. */}
      <input ref={jdInputRef} type="file" accept={JD_ACCEPT} style={{ display: 'none' }} onChange={onJdChosen} />

      {/* ── Postings table ──────────────────────────────────────────────────── */}
      <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px' }}>
        <div className="card-body">
          <h6 style={{ color: brand.deepBlue, fontWeight: 800, marginBottom: '1rem' }}>
            All Postings ({openings.length})
          </h6>

          {isLoading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
          ) : openings.length === 0 ? (
            <div style={{ color: brand.charcoal, fontSize: '0.875rem', padding: '1rem 0' }}>
              No postings yet. Click “New posting” to create one.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ color: brand.charcoal, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                    <th>Position</th>
                    <th>Visibility</th>
                    <th>Manager</th>
                    <th>Location</th>
                    <th>Type</th>
                    <th>Salary</th>
                    <th>Status</th>
                    <th>Applicants</th>
                    <th>File</th>
                    <th>Video</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {openings.map((o) => {
                    const fulfilled = (o.fulfilled_by || '').trim();
                    const apps = appsByOpening[o.id] || [];
                    const showApps = expandedApps.has(o.id) && apps.length > 0;
                    return (
                    <React.Fragment key={o.id}>
                    <tr style={{ opacity: fulfilled ? 0.55 : (o.is_active ? 1 : 0.6), background: fulfilled ? '#f6f4ef' : undefined }}>
                      <td>
                        <div style={{ fontWeight: 700, color: brand.deepBlue }}>{o.job_title}</div>
                        <div style={{ color: brand.charcoal, fontSize: '0.72rem' }}>
                          {[labelFor(EMPLOYMENT_TYPES, o.employment_type), o.is_intern ? 'Internship' : null]
                            .filter(Boolean).join(' · ')}
                          {o.potential_start_date ? ` · starts ${o.potential_start_date}` : ''}
                          {fulfilled ? ` · filled by ${fulfilled}` : ''}
                        </div>
                      </td>
                      <td><VisibilityBadge visibility={o.visibility} /></td>
                      <td>{o.manager_name || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                      <td>{o.location || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                      <td>{labelFor(POSITION_TYPES, o.position_type) || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                      <td>{o.salary_range || <span style={{ color: '#b9c3cd' }}>—</span>}</td>
                      <td>
                        {fulfilled ? (
                          <span title={`Filled by ${fulfilled} — hidden from the employee/manager board`} style={{
                            background: brand.goldenrod, color: brand.deepBlue,
                            fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: '999px',
                            display: 'inline-flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap',
                          }}>
                            <i className="bi bi-check-circle-fill" /> Fulfilled
                          </span>
                        ) : (
                          <span style={{
                            background: o.is_active ? brand.green : brand.charcoal, color: 'white',
                            fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.55rem', borderRadius: '999px',
                          }}>
                            {o.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const apps = appsByOpening[o.id] || [];
                          if (apps.length === 0) return <span style={{ color: '#b9c3cd' }}>—</span>;
                          const open = expandedApps.has(o.id);
                          return (
                            <button
                              className="btn btn-sm btn-link p-0"
                              style={{ color: brand.tealBlue, fontWeight: 700, whiteSpace: 'nowrap' }}
                              onClick={() => toggleApps(o.id)}
                              title={open ? 'Hide applications' : 'Show applications'}
                            >
                              <i className={`bi bi-chevron-${open ? 'down' : 'right'} me-1`} />
                              <i className="bi bi-people-fill me-1" />{apps.length}
                            </button>
                          );
                        })()}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {o.jd_filename ? (
                          <>
                            <button className="btn btn-sm btn-link p-0 me-2" style={{ color: brand.tealBlue }} title={o.jd_filename} onClick={() => onDownload(o.id)}>
                              <i className="bi bi-file-earmark-text" />
                            </button>
                            <button className="btn btn-sm btn-link p-0" style={{ color: brand.red }} title="Remove file" onClick={() => onRemoveJd(o)}>
                              <i className="bi bi-x-circle" />
                            </button>
                          </>
                        ) : (
                          <button className="btn btn-sm btn-link p-0" style={{ color: brand.charcoal }} title="Upload JD file" onClick={() => triggerUpload(o.id)}>
                            <i className="bi bi-upload" /> Upload JD
                          </button>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {o.video_filename ? (
                          <span style={{ color: brand.green, fontWeight: 700 }} title={o.video_filename}>
                            <i className="bi bi-camera-video-fill me-1" /> Yes
                          </span>
                        ) : (
                          <span style={{ color: brand.charcoal }}>No</span>
                        )}
                      </td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm btn-link p-0 me-2" style={{ color: brand.goldenrod }} title="Email the new-posting notification to the configured recipient list" onClick={() => onAnnounce(o)}>
                          <i className="bi bi-megaphone" />
                        </button>
                        <button className="btn btn-sm btn-link p-0 me-2" style={{ color: brand.tealBlue }} title="Edit" onClick={() => openEdit(o)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button className="btn btn-sm btn-link p-0" style={{ color: brand.red }} title="Delete" onClick={() => onDelete(o)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                    {showApps && (
                      <tr>
                        <td colSpan={11} style={{ background: '#f4f1ec', padding: '1rem' }}>
                          <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.8rem', marginBottom: '0.6rem' }}>
                            <i className="bi bi-people-fill me-1" /> {apps.length} application{apps.length > 1 ? 's' : ''} for “{o.job_title}”
                          </div>
                          {apps.map((a) => <ApplicationCard key={a.id} app={a} />)}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
